/**
 * Entitlements: qué puede hacer una organización en un módulo, según su suscripción.
 * El núcleo es la fuente de verdad de los valores; el módulo aplica el límite en su BD.
 */
import { z } from "zod";
import { PATTERNS } from "./constants.js";
export const FeatureValueSchema = z.union([z.boolean(), z.number(), z.string()]);
export const SubscriptionStatusSchema = z.enum([
    "trialing",
    "active",
    "past_due",
    "suspended",
    "cancelled",
]);
export const LimitStateSchema = z.object({
    /** null = ilimitado. */
    max: z.number().nullable(),
    /** Último valor reportado por el módulo (0 si nunca reportó). */
    used: z.number().nonnegative(),
    aggregation: z.enum(["gauge", "counter_monthly"]),
    /** Periodo al que corresponde `used` (YYYY-MM-DD; `1970-01-01` para gauge). */
    period: z.iso.date(),
    /**
     * Precio por unidad por encima de `max` (docs/FASE-6.md §9). `null` = el tope es duro y no hay
     * excedente que facturar. Con precio, `max` es la cantidad incluida.
     */
    overageUnitAmount: z.number().nullable().optional(),
    /**
     * `hard` = el módulo bloquea al llegar a `max`. `soft` = puede seguir y el exceso se factura.
     *
     * **Opcional a propósito, también en el tipo de salida.** Con `.default("hard")` el campo pasaría a
     * ser obligatorio al *construir* un `LimitState`, y eso rompe en tiempo de compilación los tests de
     * los cuatro módulos que arman snapshots a mano — justo lo contrario de lo que esta fase promete.
     * Ausente significa `hard`, que es como se comportaba todo antes de la fase 6.
     */
    enforcement: z.enum(["hard", "soft"]).optional(),
});
/**
 * Instantánea completa de entitlements de una organización en un módulo.
 * `version` coincide con el claim `ent_v` del token: si el token trae una versión mayor que la
 * cacheada, el módulo debe refrescar.
 */
export const EntitlementsSnapshotSchema = z.object({
    orgId: z.uuid(),
    moduleCode: z.string().regex(PATTERNS.MODULE_CODE),
    version: z.number().int().nonnegative(),
    /** false cuando no hay suscripción vigente: el módulo debe denegar el acceso. */
    active: z.boolean(),
    status: SubscriptionStatusSchema.nullable(),
    plan: z
        .object({
        code: z.string(),
        name: z.string(),
        billingModel: z.enum(["flat", "per_seat"]),
    })
        .nullable(),
    seats: z.number().int().nonnegative().nullable(),
    features: z.record(z.string().regex(PATTERNS.KEY), FeatureValueSchema),
    limits: z.record(z.string().regex(PATTERNS.KEY), LimitStateSchema),
    /** Fin del periodo vigente (o del trial), ISO 8601; null si no aplica. */
    currentPeriodEnd: z.iso.datetime().nullable(),
    generatedAt: z.iso.datetime(),
});
/** Reporte de uso que el módulo envía al núcleo (`POST /api/v1/usage`). Valor absoluto. */
export const UsageReportSchema = z.object({
    orgId: z.uuid(),
    moduleCode: z.string().regex(PATTERNS.MODULE_CODE),
    limitKey: z.string().regex(PATTERNS.KEY),
    value: z.number().nonnegative(),
    /** Requerido para `counter_monthly` (YYYY-MM-DD, primer día del mes). Ignorado para gauge. */
    period: z.iso.date().optional(),
});
export const UsageReportBatchSchema = z.object({
    reports: z.array(UsageReportSchema).min(1).max(500),
});
/** Helpers puros para que módulos y núcleo evalúen igual. */
export function isFeatureEnabled(snapshot, key) {
    if (!snapshot.active)
        return false;
    const value = snapshot.features[key];
    if (value === undefined)
        return false;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "number")
        return value > 0;
    return value.length > 0;
}
export function remainingLimit(snapshot, key) {
    const limit = snapshot.limits[key];
    if (!limit)
        return 0;
    if (limit.max === null)
        return null;
    return Math.max(0, limit.max - limit.used);
}
/** true si agregar `delta` unidades al límite `key` sigue dentro del máximo. */
export function canConsume(snapshot, key, delta = 1) {
    if (!snapshot.active)
        return false;
    const remaining = remainingLimit(snapshot, key);
    return remaining === null || remaining >= delta;
}
/**
 * Como `canConsume`, pero admite pasarse del tope cuando el límite tiene precio de excedente
 * (`enforcement: "soft"`): el exceso se factura al cerrar el periodo (docs/FASE-6.md §9.3).
 *
 * `canConsume` **no** cambia de semántica: sigue devolviendo `false` al llegar al tope. Un módulo
 * que no adopte esta función se comporta exactamente igual que antes de la fase 6, que es lo que
 * hace seguro publicarla con cuatro módulos en marcha.
 */
export function canConsumeOrBill(snapshot, key, delta = 1) {
    if (!snapshot.active)
        return false;
    if (canConsume(snapshot, key, delta))
        return true;
    return snapshot.limits[key]?.enforcement === "soft";
}
//# sourceMappingURL=entitlements.js.map